<template>
  <div class="conversation-sidebar-wrap opportunity-sidebar-wrap">
    <woot-loading-state v-if="uiFlags.isFetching" />

    <div
      v-else-if="opportunity"
      :class="{ 'opportunity-sidebar--form': showNegotiationForm }"
    >
      <opportunity-panel-header @close="onDismiss">
        <template v-if="showNegotiationForm">
          {{ $t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.TITLE') }}
        </template>
        <spinner v-else-if="isLoadingPipelineMeta" size="small" />
        <template v-else>
          {{
            $t('CONVERSATION_SIDEBAR.OPPORTUNITIES.PIPELINE', {
              pipelineName,
            })
          }}
        </template>
      </opportunity-panel-header>
      <div v-if="showNegotiationForm" class="negotiation-panel">
        <opportunity-negotiation-form
          :opportunity="opportunity"
          :is-locked="isOpportunityLocked"
          :pending-question-ids="negotiationPendingQuestionIds"
          :target-stage="negotiationTargetStage"
          @saved="onNegotiationFormSaved"
        />
      </div>
      <div v-else class="sidebar-content">
        <opportunity-stage-select
          :value="opportunity && opportunity.pipeline_stage_id"
          :stages="activeTimelineStages"
          :current-stage-id="opportunity && opportunity.pipeline_stage_id"
          :label="$t('OPPORTUNITY_DETAILS.STATUS_MODAL.PIPELINE_STAGE')"
          :disabled="
            isOpportunityLocked || isSubmittingStage || !canWriteOpportunities
          "
          :loading="isLoadingPipelineMeta"
          panel
          class="sidebar-stage-select"
          @input="handleStageChange"
        />

        <opportunity-details-card
          :opportunity="opportunity"
          :products="products"
          :can-mark-won="canMarkWon"
          :can-mark-lost="canMarkLost"
          :can-cancel="canCancelOpportunity"
          :is-locked="isOpportunityLocked"
          :redirect-button="true"
          :is-loading-products="isLoadingRelations"
          :negotiation-form-enabled="negotiationFormEnabled"
          @open-status-modal="openStatusModal"
          @refresh-products="loadRelations(opportunity)"
          @open-negotiation-form="openNegotiationForm"
        />

        <opportunity-activities
          :opportunity="opportunity"
          :is-locked="isOpportunityLocked"
        />

        <opportunity-notes
          :notes="notes"
          :is-locked="isOpportunityLocked"
          :is-submitting="isSubmittingNote"
          :is-loading="isLoadingRelations"
          @add-note="submitNote"
        />
      </div>

      <opportunity-modals
        v-if="opportunity"
        :opportunity="opportunity"
        :show-status-dialog="showStatusDialog"
        :status-action="statusAction"
        :active-timeline-stages="activeTimelineStages"
        :active-loss-reasons="activeLossReasons"
        :is-submitting-status="isSubmittingStatus"
        @close-status="closeStatusModal"
        @submit-status="submitStatusChange"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import Spinner from 'shared/components/Spinner';
import opportunityWritePermission from 'dashboard/mixins/opportunityWritePermission';
import { sortStagesByPosition } from 'dashboard/helper/opportunityPipelineHelper';
import {
  hasActiveNegotiationForm,
  pendingQuestionIdsFromError,
} from 'dashboard/helper/opportunityNegotiationFormHelper';
import OpportunityNotes from '../opportunities/components/OpportunityNotes.vue';
import OpportunityDetailsCard from '../opportunities/components/OpportunityDetailsCard.vue';
import OpportunityModals from '../opportunities/components/OpportunityModals.vue';
import OpportunityStageSelect from '../opportunities/components/OpportunityStageSelect.vue';
import OpportunityActivities from '../opportunities/components/activities/OpportunityActivities.vue';
import OpportunityNegotiationForm from '../opportunities/components/negotiationForm/OpportunityNegotiationForm.vue';
import OpportunityPanelHeader from '../opportunities/components/OpportunityPanelHeader.vue';

export default {
  name: 'OpportunitySidebarDetails',
  components: {
    OpportunityNotes,
    OpportunityDetailsCard,
    OpportunityModals,
    OpportunityStageSelect,
    OpportunityActivities,
    OpportunityNegotiationForm,
    OpportunityPanelHeader,
    Spinner,
  },
  mixins: [alertMixin, opportunityWritePermission],
  props: {
    opportunity: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isSubmittingNote: false,
      isSubmittingStatus: false,
      isSubmittingStage: false,
      isLoadingRelations: false,
      isLoadingPipelineMeta: false,
      loadedOpportunityId: null,
      notes: [],
      products: [],
      timelineStages: [],
      lossReasons: [],
      pipelineStatus: 'active',
      pipelineName: '',
      pipelineMetaTimer: null,
      showStatusDialog: false,
      statusAction: 'won',
      negotiationFormEnabled: false,
      showNegotiationForm: false,
      negotiationPendingQuestionIds: [],
      negotiationTargetStage: null,
      negotiationRetry: null,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'opportunities/getUIFlags',
    }),
    activeLossReasons() {
      return this.lossReasons.filter(reason => reason.status !== 'inactive');
    },
    activeTimelineStages() {
      return this.timelineStages.filter(
        stage => stage.status !== 'inactive' || this.isCurrentStage(stage)
      );
    },
    isPipelineInactive() {
      return this.pipelineStatus === 'inactive';
    },
    isOpportunityLocked() {
      return (
        !!this.opportunity &&
        (this.opportunity.status !== 'open' || this.isPipelineInactive)
      );
    },
    canMarkWon() {
      return !!this.opportunity && !this.isOpportunityLocked;
    },
    canMarkLost() {
      return !!this.opportunity && !this.isOpportunityLocked;
    },
    canCancelOpportunity() {
      return (
        !!this.opportunity &&
        this.opportunity.status !== 'canceled' &&
        !this.isPipelineInactive
      );
    },
  },
  watch: {
    opportunity: {
      immediate: true,
      handler(newOpp, oldOpp) {
        if (!newOpp || !oldOpp || Number(newOpp.id) !== Number(oldOpp.id)) {
          this.closeNegotiationForm();
          this.closeStatusModal();
        }
        if (newOpp && newOpp.id) {
          this.loadRelations(newOpp);
        } else {
          this.resetState();
        }
      },
    },
  },
  mounted() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.pipelineMetaTimer = window.setInterval(
      this.refreshPipelineMetaSilently,
      20000
    );
    window.bus.$on('opportunity_updated', this.handleOpportunityUpdatedEvent);
  },
  beforeDestroy() {
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    if (this.pipelineMetaTimer) {
      window.clearInterval(this.pipelineMetaTimer);
    }
    window.bus.$off('opportunity_updated', this.handleOpportunityUpdatedEvent);
  },
  methods: {
    resetState() {
      this.negotiationFormEnabled = false;
      this.notes = [];
      this.products = [];
      this.timelineStages = [];
      this.lossReasons = [];
      this.pipelineName = '';
      this.loadedOpportunityId = null;
      this.isLoadingRelations = false;
      this.isLoadingPipelineMeta = false;
    },
    async refreshOpportunity() {
      if (!(this.opportunity && this.opportunity.conversation_id)) {
        return;
      }
      await this.$store.dispatch('opportunities/fetchByConversation', {
        conversationId: this.opportunity.conversation_id,
      });
    },
    isCurrentOpportunity(opportunityId) {
      return (
        !!this.opportunity &&
        Number(this.opportunity.id) === Number(opportunityId)
      );
    },
    async loadRelations(currentOpp) {
      const opportunityId = currentOpp.id;

      if (this.loadedOpportunityId !== opportunityId) {
        this.isLoadingRelations = true;
        this.isLoadingPipelineMeta = true;
      }

      try {
        await Promise.all([
          this.loadNotesAndProducts(opportunityId),
          this.loadPipelineMeta(opportunityId, currentOpp.pipeline_id),
        ]);
        if (this.isCurrentOpportunity(opportunityId)) {
          this.loadedOpportunityId = opportunityId;
        }
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_DETAILS.ERRORS.LOAD'));
      }
    },
    async loadNotesAndProducts(opportunityId) {
      try {
        const [notes, products] = await Promise.all([
          this.$store.dispatch('opportunities/getNotes', opportunityId),
          this.$store.dispatch('opportunities/getProducts', opportunityId),
        ]);
        if (!this.isCurrentOpportunity(opportunityId)) return;

        this.notes = notes || [];
        this.products = products || [];
      } finally {
        if (this.isCurrentOpportunity(opportunityId)) {
          this.isLoadingRelations = false;
        }
      }
    },
    async loadPipelineMeta(opportunityId, pipelineId) {
      try {
        await this.fetchPipelineMeta(pipelineId, () =>
          this.isCurrentOpportunity(opportunityId)
        );
      } finally {
        if (this.isCurrentOpportunity(opportunityId)) {
          this.isLoadingPipelineMeta = false;
        }
      }
    },
    async fetchPipelineMeta(pipelineId, isStillRelevant = () => true) {
      if (!pipelineId) return;
      const [pipeline, stages, reasons] = await Promise.all([
        this.$store.dispatch('opportunityPipelines/show', pipelineId),
        this.$store.dispatch('opportunityPipelines/getStages', pipelineId),
        this.$store.dispatch('opportunityPipelines/getLossReasons', pipelineId),
      ]);
      if (!isStillRelevant()) return;

      this.pipelineStatus = pipeline ? pipeline.status : 'active';
      this.pipelineName = pipeline ? pipeline.name : '';
      this.negotiationFormEnabled = hasActiveNegotiationForm(pipeline);
      this.timelineStages = sortStagesByPosition(stages || []);
      this.lossReasons = reasons || [];
    },
    async refreshPipelineMetaSilently() {
      if (!(this.opportunity && this.opportunity.pipeline_id)) {
        return;
      }

      try {
        await this.fetchPipelineMeta(this.opportunity.pipeline_id);
      } catch (error) {
        // Background sync failure is non-critical
      }
    },
    async handleOpportunityUpdatedEvent(data) {
      if (
        !this.opportunity ||
        Number(data?.id) !== Number(this.opportunity.id)
      ) {
        return;
      }

      const opportunity = await this.$store
        .dispatch('opportunities/getById', this.opportunity.id)
        .catch(() => null);

      if (
        opportunity &&
        opportunity.updated_at !== this.opportunity.updated_at
      ) {
        this.$store.dispatch('opportunities/refreshIfCurrent', opportunity);
      }
    },
    onPanelToggle() {
      this.$emit('toggle-opportunity-sidebar');
    },
    onDismiss() {
      if (this.showNegotiationForm) {
        this.closeNegotiationForm();
        return;
      }
      this.onPanelToggle();
    },
    openNegotiationForm(
      pendingQuestionIds = [],
      targetStage = null,
      retry = null
    ) {
      this.negotiationPendingQuestionIds = pendingQuestionIds;
      this.negotiationTargetStage = targetStage;
      this.negotiationRetry = retry;
      this.showNegotiationForm = true;
    },
    async onNegotiationFormSaved() {
      const retry = this.negotiationRetry;
      this.closeNegotiationForm();
      if (retry) {
        await retry();
      }
    },
    closeNegotiationForm() {
      this.showNegotiationForm = false;
      this.negotiationPendingQuestionIds = [];
      this.negotiationTargetStage = null;
      this.negotiationRetry = null;
    },
    async handleStageChange(newStageId, { checkNegotiationForm = true } = {}) {
      if (
        !(this.opportunity && this.opportunity.id) ||
        this.isOpportunityLocked ||
        this.isSubmittingStage ||
        !this.canWriteOpportunities ||
        newStageId == null ||
        Number(newStageId) === Number(this.opportunity.pipeline_stage_id)
      ) {
        return;
      }

      const opportunityId = this.opportunity.id;
      const moveAfterForm = () =>
        this.isCurrentOpportunity(opportunityId) &&
        this.handleStageChange(newStageId, { checkNegotiationForm: false });
      this.isSubmittingStage = true;
      try {
        if (
          checkNegotiationForm &&
          (await this.openNegotiationFormIfPending(newStageId, moveAfterForm))
        ) {
          return;
        }
        await this.$store.dispatch('opportunities/moveStage', {
          opportunityId,
          pipelineStageId: newStageId,
        });
        await this.refreshOpportunity();
      } catch (error) {
        const backendMessage =
          error && error.response && error.response.data
            ? error.response.data.message
            : null;
        this.showAlert(
          backendMessage || this.$t('OPPORTUNITY_DETAILS.ERRORS.STATUS')
        );
        await this.refreshOpportunity();
        if (this.isCurrentOpportunity(opportunityId)) {
          this.openNegotiationFormForPending(error, newStageId, moveAfterForm);
        }
      } finally {
        this.isSubmittingStage = false;
      }
    },
    findTimelineStage(stageId) {
      return (
        this.timelineStages.find(
          stage => Number(stage.id) === Number(stageId)
        ) || null
      );
    },
    async openNegotiationFormIfPending(targetStageId, retry) {
      const { opportunity } = this;
      const targetStage = this.findTimelineStage(targetStageId);
      if (!this.negotiationFormEnabled || !targetStage) {
        return false;
      }

      let pendingQuestionIds;
      try {
        pendingQuestionIds = await this.$store.dispatch(
          'opportunities/pendingNegotiationQuestionIds',
          { opportunity, stage: targetStage }
        );
      } catch (error) {
        return false;
      }
      if (!pendingQuestionIds || !pendingQuestionIds.length) {
        return false;
      }
      if (this.isCurrentOpportunity(opportunity.id)) {
        this.openNegotiationForm(pendingQuestionIds, targetStage, retry);
      }
      return true;
    },
    openNegotiationFormForPending(error, targetStageId, retry) {
      const pendingQuestionIds = pendingQuestionIdsFromError(error);
      if (!this.negotiationFormEnabled || !pendingQuestionIds.length) {
        return false;
      }
      this.openNegotiationForm(
        pendingQuestionIds,
        this.findTimelineStage(targetStageId),
        retry
      );
      return true;
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.refreshPipelineMetaSilently();
      }
    },
    isCurrentStage(stage) {
      return (
        Number(stage.id) ===
        Number(this.opportunity && this.opportunity.pipeline_stage_id)
      );
    },
    async submitNote(content, onFailure) {
      if (
        !content ||
        !(this.opportunity && this.opportunity.id) ||
        this.isOpportunityLocked
      ) {
        return;
      }

      this.isSubmittingNote = true;
      try {
        await this.$store.dispatch('opportunities/createNote', {
          opportunityId: this.opportunity.id,
          content,
        });
        this.notes =
          (await this.$store.dispatch(
            'opportunities/getNotes',
            this.opportunity.id
          )) || [];
      } catch (error) {
        if (onFailure) onFailure();
        this.showAlert(this.$t('OPPORTUNITY_DETAILS.ERRORS.ADD_NOTE'));
      } finally {
        this.isSubmittingNote = false;
      }
    },
    openStatusModal(action) {
      const isActionAllowed = {
        won: this.canMarkWon,
        lost: this.canMarkLost,
        canceled: this.canCancelOpportunity,
      }[action];

      if (!isActionAllowed) {
        return;
      }

      this.statusAction = action;
      this.showStatusDialog = true;
    },
    closeStatusModal() {
      this.showStatusDialog = false;
    },
    async submitStatusChange(statusForm, { checkNegotiationForm = true } = {}) {
      if (!(this.opportunity && this.opportunity.id)) {
        return;
      }

      const { opportunity } = this;
      const submitAfterForm = () =>
        this.isCurrentOpportunity(opportunity.id) &&
        this.submitStatusChange(statusForm, { checkNegotiationForm: false });
      this.isSubmittingStatus = true;

      try {
        const selectedStageId = statusForm.pipelineStageId;
        if (
          selectedStageId &&
          Number(selectedStageId) !== Number(opportunity.pipeline_stage_id)
        ) {
          if (
            checkNegotiationForm &&
            (await this.openNegotiationFormIfPending(
              selectedStageId,
              submitAfterForm
            ))
          ) {
            this.closeStatusModal();
            return;
          }
          await this.$store.dispatch('opportunities/moveStage', {
            opportunityId: opportunity.id,
            pipelineStageId: selectedStageId,
          });
        }

        if (statusForm.note && statusForm.note.trim()) {
          await this.$store.dispatch('opportunities/createNote', {
            opportunityId: opportunity.id,
            content: statusForm.note.trim(),
          });
        }

        await this.$store.dispatch('opportunities/updateStatus', {
          opportunityId: opportunity.id,
          status: this.statusAction,
          lossReasonId:
            this.statusAction === 'lost' ? statusForm.lossReasonId : null,
        });

        if (!this.isCurrentOpportunity(opportunity.id)) return;
        await this.refreshOpportunity();
        this.closeStatusModal();
      } catch (error) {
        const backendMessage =
          error && error.response && error.response.data
            ? error.response.data.message
            : null;
        this.showAlert(
          backendMessage || this.$t('OPPORTUNITY_DETAILS.ERRORS.STATUS')
        );
        if (
          this.isCurrentOpportunity(opportunity.id) &&
          this.openNegotiationFormForPending(
            error,
            statusForm.pipelineStageId,
            submitAfterForm
          )
        ) {
          this.closeStatusModal();
        }
      } finally {
        this.isSubmittingStatus = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/woot';

.opportunity-sidebar-wrap {
  overflow-y: auto;

  @include breakpoint(medium up) {
    flex-basis: 35rem;
  }
  @include breakpoint(large up) {
    flex-basis: 40rem;
  }
  @include breakpoint(xlarge up) {
    flex-basis: 45rem;
  }
  @include breakpoint(xxlarge up) {
    flex-basis: 50rem;
  }
}

.sidebar-content {
  padding: var(--space-normal);
  display: flex;
  flex-direction: column;
  gap: var(--space-normal);
}

.sidebar-stage-select {
  background: var(--v-50);
  border-radius: var(--border-radius-normal);
  border: 1px solid var(--color-border);
}

.opportunity-sidebar--form {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.negotiation-panel {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}
</style>
